import { pdfjs } from "react-pdf";

// The worker that matches react-pdf's own pdf.js (copied by
// scripts/copy-pdf-worker.js before every start and build).
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

/** Pages drawn this wide: sharp on a big screen at 100%, a few hundred KB. */
const RENDER_WIDTH = 1400;
/** A lesson's worth. More pages than this is a textbook, not a slide. */
const MAX_PAGES = 40;

const isPdf = (file) => file.type === "application/pdf" || /\.pdf$/i.test(file.name);
const isImage = (file) => /^image\//.test(file.type);

const imageSize = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({ width: 4, height: 3 });
    img.src = url;
  });

const canvasToFile = (canvas, name) =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(new File([blob], name, { type: "image/jpeg" })) : reject(new Error("render failed"))),
      "image/jpeg",
      0.86
    )
  );

/**
 * Turn the files a teacher opened into pictures ready for the board.
 *
 * Each picture is uploaded (`upload(file) -> url`) so that every screen in
 * the room can load it — a file on the teacher's computer reaches nobody
 * else. A PDF becomes one picture per page, drawn here in the teacher's
 * browser: the students then only ever load images, with nothing to parse.
 *
 * Without `upload` (a preview with no session) pictures stay local.
 * `onProgress(done, total)` reports pages as they go up.
 *
 * Returns [{ url, name, page, pages, ratio }] — ratio is height / width.
 * Files that are neither images nor PDFs are skipped and named in `skipped`.
 */
export async function prepareContent(fileList, { upload, onProgress } = {}) {
  const files = [...(fileList || [])];
  const out = [];
  const skipped = [];

  // Count the work first, so progress means something.
  const plans = [];
  for (const file of files) {
    if (isImage(file)) plans.push({ file, kind: "image", pages: 1 });
    else if (isPdf(file)) {
      const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
      plans.push({ file, kind: "pdf", doc, pages: Math.min(doc.numPages, MAX_PAGES) });
    } else skipped.push(file.name);
  }
  const total = plans.reduce((n, p) => n + p.pages, 0);
  let done = 0;
  onProgress?.(0, total);

  for (const plan of plans) {
    if (plan.kind === "image") {
      const local = URL.createObjectURL(plan.file);
      const { width, height } = await imageSize(local);
      const url = upload ? (await upload(plan.file)).url : local;
      out.push({ url, name: plan.file.name, page: null, pages: 1, ratio: height / width });
      onProgress?.((done += 1), total);
      continue;
    }
    for (let n = 1; n <= plan.pages; n += 1) {
      const page = await plan.doc.getPage(n);
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: RENDER_WIDTH / base.width });
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(viewport.width);
      canvas.height = Math.round(viewport.height);
      const ctx = canvas.getContext("2d");
      // Paper is white; a transparent page would show the board through it.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({ canvasContext: ctx, viewport }).promise;
      const name = `${plan.file.name.replace(/\.pdf$/i, "")} · page ${n}.jpg`;
      const image = await canvasToFile(canvas, name);
      const url = upload ? (await upload(image)).url : URL.createObjectURL(image);
      out.push({ url, name: plan.file.name, page: n, pages: plan.doc.numPages, ratio: canvas.height / canvas.width });
      onProgress?.((done += 1), total);
    }
  }
  return { items: out, skipped, truncated: plans.some((p) => p.kind === "pdf" && p.doc.numPages > MAX_PAGES) };
}
